import axios from "axios";

const API_BASE = "http://127.0.0.1:8000/api";

const API = {
  deviceAssignments: `${API_BASE}/devices/device-assignments/?format=json`,
  admissions: `${API_BASE}/patients/admissions/?format=json`,
  hospitals: `${API_BASE}/hospitals/?format=json`,
};

function getHeaders() {
  const accessToken = localStorage.getItem("access_token");
  const token = localStorage.getItem("token");

  const headers = {
    "Content-Type": "application/json",
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  } else if (token) {
    headers.Authorization = `Token ${token}`;
  }

  return headers;
}

function unwrapResponse(res) {
  const data = res?.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;

  return data ?? [];
}

function unwrapSingleResponse(res) {
  return res?.data ?? null;
}

function normalizeAxiosError(error) {
  if (error?.response?.data) {
    throw error.response.data;
  }

  if (error?.message) {
    throw { detail: error.message };
  }

  throw { detail: "Unknown API error" };
}

function getAssignmentId(row) {
  return (
    row?.device_assignment_id ??
    row?.assignment_id ??
    row?.id ??
    row?.uuid ??
    ""
  );
}

async function apiGet(url) {
  try {
    const res = await axios.get(url, {
      headers: getHeaders(),
    });
    return unwrapResponse(res);
  } catch (error) {
    normalizeAxiosError(error);
  }
}

async function apiGetOne(url) {
  try {
    const res = await axios.get(url, {
      headers: getHeaders(),
    });
    return unwrapSingleResponse(res);
  } catch (error) {
    normalizeAxiosError(error);
  }
}

async function apiPost(url, payload) {
  try {
    const res = await axios.post(url, payload, {
      headers: getHeaders(),
    });
    return unwrapSingleResponse(res);
  } catch (error) {
    normalizeAxiosError(error);
  }
}

async function apiPatch(url, payload) {
  try {
    const res = await axios.patch(url, payload, {
      headers: getHeaders(),
    });
    return unwrapSingleResponse(res);
  } catch (error) {
    normalizeAxiosError(error);
  }
}

async function apiPut(url, payload) {
  try {
    const res = await axios.put(url, payload, {
      headers: getHeaders(),
    });
    return unwrapSingleResponse(res);
  } catch (error) {
    normalizeAxiosError(error);
  }
}

async function apiDelete(url) {
  try {
    const res = await axios.delete(url, {
      headers: getHeaders(),
    });
    return unwrapSingleResponse(res);
  } catch (error) {
    normalizeAxiosError(error);
  }
}

// ----------------------------
// list apis
// ----------------------------
export async function listDeviceAssignments() {
  return apiGet(API.deviceAssignments);
}

export async function listAdmissions() {
  return apiGet(API.admissions);
}

export async function listHospitals() {
  return apiGet(API.hospitals);
}

// ----------------------------
// single detail api
// ----------------------------
export async function getDeviceAssignmentById(assignmentId) {
  try {
    return await apiGetOne(
      `${API_BASE}/devices/device-assignments/${assignmentId}/?format=json`
    );
  } catch (error) {
    const rows = await listDeviceAssignments();

    return (
      rows.find((row) => {
        const id = getAssignmentId(row);
        return String(id) === String(assignmentId);
      }) || null
    );
  }
}

// ----------------------------
// create api
// ----------------------------
export async function createDeviceAssignment(payload) {
  return apiPost(API.deviceAssignments, payload);
}

// ----------------------------
// update apis
// ----------------------------
export async function updateDeviceAssignment(assignmentId, payload) {
  return apiPut(
    `${API_BASE}/devices/device-assignments/${assignmentId}/?format=json`,
    payload
  );
}

export async function patchDeviceAssignment(assignmentId, payload) {
  return apiPatch(
    `${API_BASE}/devices/device-assignments/${assignmentId}/?format=json`,
    payload
  );
}

export async function unassignDeviceAssignment(
  assignmentId,
  deviceId,
  unassignedAt
) {
  return apiPatch(
    `${API_BASE}/devices/device-assignments/${assignmentId}/?format=json`,
    {
      device_id: deviceId,
      unassigned_at: unassignedAt,
    }
  );
}

// ----------------------------
// delete api
// ----------------------------
export async function deleteDeviceAssignment(assignmentId) {
  return apiDelete(
    `${API_BASE}/devices/device-assignments/${assignmentId}/?format=json`
  );
}